import {
  IncludeExcludeToggle,
  IncludeExcludeValue,
} from "@/components/IncludeExcludeToggle";
import { NewTag, Tag } from "@/components/Tag";
import { GradientCard } from "@/containers/GradientCard";
import { useTokenCosts } from "@/design-system/Evolution/useTokenCosts";
import { Popover } from "@/design-system/Popover/Popover";
import { EvoDetailPanel } from "@/features/Evolutions/EvoDetailPanel";
import { expire as expireIcon, info } from "@/icons";
import { repeatable } from "@/icons";
import {
  Evolution,
  evolutionCategoriesByIdSelector,
} from "@/redux/evolutions/evolutionsSlice";
import { costTokens, formatEvoCost } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { evolutionOverviewPath, formatTime } from "@repo/shared/utils";
import { Dna, ExternalLink } from "lucide-react";
import { useSelector } from "react-redux";

interface EvolutionFilterCardProps {
  evolution: Evolution;
  filterState: IncludeExcludeValue;
  onInclude: () => void;
  onExclude: () => void;
  onMustInclude: () => void;
  isExpired?: boolean;
}

export const EvolutionFilterCard = ({
  evolution,
  filterState,
  onInclude,
  onExclude,
  onMustInclude,
  isExpired,
}: EvolutionFilterCardProps) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const tokenCosts = useTokenCosts(costTokens(evolution.cost));
  const timeRemaining = formatTime(evolution.endTime * 1000 - Date.now());
  const categoriesById = useSelector(evolutionCategoriesByIdSelector);
  const category = categoriesById[evolution.categoryId];
  return (
    <GradientCard padding="p-3">
      <div className="flex flex-col gap-y-4">
        {/* Header with icon and name */}
        <div className="flex flex-row justify-between">
          <div className="flex items-center gap-x-0.5">
            {/* Icon placeholder */}

            <Dna size={16} className="text-emerald-500" />
            <span className="text-sm font-semibold text-dark-secondary px-1">
              {evolution.name}
            </span>
            {evolution.newEvolution && <NewTag />}
            {isExpired && (
              <Tag
                tag={t("expired")}
                type="custom"
                tagDesignCustomData="bg-dark-disabled"
              />
            )}
          </div>
          <Popover
            trigger={
              <button
                className="text-dark-tertiary size-4 shrink-0 cursor-pointer transition-colors hover:text-dark-primary"
                aria-label={t("picker.details", { name: evolution.name })}
              >
                {info}
              </button>
            }
            width={900}
            align="end"
            negLabel=""
            posLabel=""
          >
            <div className="flex flex-col gap-4">
              <EvoDetailPanel
                evolution={evolution}
                category={category}
                variant="overview"
              />
              <div className="flex justify-end border-t border-neutral-700 pt-3">
                <a
                  href={evolutionOverviewPath(evolution)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-row items-center gap-x-1.5 text-sm font-medium text-dark-link hover:text-dark-link-hover"
                >
                  {t("picker.seeFullDetails")}
                  <ExternalLink className="size-4" />
                </a>
              </div>
            </div>
          </Popover>
        </div>

        {/* Stats row */}
        <div className="flex gap-x-2 h-8">
          {/* Time remaining */}

          {!isExpired && (
            <div className="flex items-center gap-x-1 bg-panel-middleground rounded-lg p-2">
              <div className="size-3 fill-dark-secondary">{expireIcon}</div>
              <span className="text-xs text-dark-secondary">
                {timeRemaining}
              </span>
            </div>
          )}
          {/* Coins */}
          {evolution.cost?.coins !== undefined && evolution.cost.coins > 0 && (
            <div className="flex items-center gap-x-1 bg-panel-middleground rounded-lg p-2 w-[62px]">
              <img
                alt={t("common:alt.coins")}
                className="size-3"
                src={
                  import.meta.env.VITE_APP_ASSETS_BASE_URL +
                  "/misc/coin-tmp.png"
                }
              />
              <span className="text-xs text-dark-secondary">
                {formatEvoCost(evolution.cost.coins)}
              </span>
            </div>
          )}

          {/* Points */}
          {evolution.cost?.points !== undefined &&
            evolution.cost.points > 0 && (
              <div className="flex items-center gap-x-1 bg-panel-middleground rounded-lg p-2 w-[62px]">
                <img
                  alt={t("common:alt.points")}
                  className="size-3"
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fc24/misc/points.png"
                  }
                />
                <span className="text-xs text-dark-secondary">
                  {formatEvoCost(evolution.cost.points)}
                </span>
              </div>
            )}

          {/* Tokens */}
          {tokenCosts.map((token) => (
            <div
              key={token.eventTokenId}
              className="flex items-center gap-x-1 bg-panel-middleground rounded-lg p-2 w-[62px]"
            >
              <img
                alt={token.name}
                className="size-3 object-contain"
                src={token.iconUrl}
              />
              <span className="text-xs text-dark-secondary">
                {formatEvoCost(token.amount)}
              </span>
            </div>
          ))}

          {/* Repeats */}
          {evolution.repeats && (
            <div className="flex items-center gap-x-1 bg-panel-middleground rounded-lg p-2">
              <div className="w-3 h-3 fill-dark-secondary">{repeatable}</div>
              <span className="text-xs text-dark-secondary">
                {evolution.repeats}
              </span>
            </div>
          )}
        </div>
        <IncludeExcludeToggle
          variant="three-state"
          value={filterState}
          onChange={(val) => {
            if (val === "mustInclude") onMustInclude();
            else if (val === "exclude") onExclude();
            else onInclude();
          }}
        />
      </div>
    </GradientCard>
  );
};
